// Task #331 — Search page: phone-number search + per-number call history.
// Task #332 — Filter the returned call history by date range, agent,
// transcript availability, and assessed status. Filters are entirely
// client-side over the rows already returned by
// ``/api/dialpad/search/calls``.
// Task #401 — the view is split into two explicit tabs (Search By
// Number / Search By Agent); each tab remembers its last search.
// Task #425 — the date filter is the shared period selector
// (TimeRangeControls), matching the Teams / Agent / Agent Profile views.
//
// User flow:
//   1. On the Number tab the user types a customer phone number (NZ
//      formats accepted — ``+64...``, ``021...``, ``64...`` or a
//      local-suffix-only run of digits); on the Agent tab they type an
//      agent name. No heuristics — each tab does exactly one thing.
//   2. After enough characters the input debounces and asks the server
//      for suggestions (visibility-scoped; phone suggestions filter out
//      ``Anonymous``).
//   3. The user can click a suggestion, or hit Enter / press Search to
//      take the top suggestion. If there are no suggestions we show
//      "No records found" and skip the call-history fetch.
//   4. On a chosen subject we render the full call history newest
//      first, with a filter bar above the search form that narrows by
//      date, agent, team, direction, transcript, and assessed status.
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  Check,
  Copy,
  ExternalLink,
  MessageSquare,
  Search,
  Send,
  X,
} from "lucide-react";

import { buildCallRefMap, linkifyCallIds, type CallRefMap } from "../lib/linkifyCallIds";
import { writeRichClipboard } from "../lib/richClipboard";
import { buildQaCopyPayload } from "./searchQaCopy";

import {
  api,
  type ApiError,
  type CallRef,
  type ClientAgentSuggestionRow,
  type ClientAgentSuggestionsOut,
  type ClientCallRow,
  type ClientPhoneSuggestionsOut,
  type ClientCallsOut,
  type CustomDateRange,
  type RangeMode,
  type SuggestedQuestionsOut,
  type TimeRange,
} from "../lib/api";
import { fmtDateTime, fmtDurationMs } from "../lib/format";
import { normalizePhone } from "../lib/phone";
import { colorStyle, groupByColor } from "../lib/questionColors";
import { buildUrl } from "../lib/router";
import { resolvePeriodBoundsMs } from "../components/TimeRangeControls.helpers";
import { TimeRangeControls, DEFAULT_RANGE_MODE } from "../components/TimeRangeControls";

const MIN_SUGGEST_CHARS = 6;
const MIN_AGENT_SUGGEST_CHARS = 2;
const DEBOUNCE_MS = 250;

// Task #401 — the Search view is split into two explicit tabs so each
// does exactly one thing: the Number tab only ever searches phone
// numbers, the Agent tab only ever searches agent names. This replaces
// the old single-input heuristic that guessed phone-vs-agent by
// inspecting whether the query contained letters.
type SearchTab = "number" | "agent";

const SEARCH_TABS: { id: SearchTab; label: string }[] = [
  { id: "number", label: "Search By Number" },
  { id: "agent", label: "Search By Agent" },
];

type SearchMode =
  | { kind: "phone"; phone: string }
  | { kind: "agent"; agentId: string; agentName: string };

type TranscriptFilter = "all" | "with" | "without";
type AssessedFilter = "all" | "assessed" | "not_assessed";
type DirectionFilter = "all" | "inbound" | "outbound";

type Filters = {
  // Task #425 — the Search view now uses the shared period selector
  // (TimeRangeControls) so the date controls match the Teams / Agent /
  // Agent Profile views: full preset list + Calendar/Rolling toggle +
  // Custom range. ``customRange`` is only meaningful when
  // ``timeRange === "custom"``.
  timeRange: TimeRange;
  rangeMode: RangeMode;
  customRange: CustomDateRange | null;
  agentId: string;     // "" = all
  team: string;        // "" = all, "__unassigned__" = blank team
  transcript: TranscriptFilter;
  assessed: AssessedFilter;
  direction: DirectionFilter;
};

const UNASSIGNED_TEAM = "__unassigned__";

// Task #425 — a fresh search defaults to the last 7 days (Weekly +
// Rolling) so it doesn't pull thousands of historical calls at once.
// This matches the old "last week" default while using the shared
// period selector's vocabulary. Users can widen the range from the
// filter bar when they need to.
const DEFAULT_TIME_RANGE: TimeRange = "weekly";

const DEFAULT_FILTERS: Filters = {
  timeRange: DEFAULT_TIME_RANGE,
  rangeMode: DEFAULT_RANGE_MODE,
  customRange: null,
  agentId: "",
  team: "",
  // Task #438 + PET-635 — searches default to the most useful slice:
  // outbound calls that have a transcript and have been assessed
  // (Mike's pilot defaults: direction=outbound, transcript=with,
  // assessed=assessed). Users can widen any of these back to "all"
  // from the filter bar after a search.
  transcript: "with",
  assessed: "assessed",
  direction: "outbound",
};

// The Calendar/Rolling choice is only meaningful for the ranges that
// support it; daily / all_time / custom force calendar (matches the
// other views' ``effectiveRangeMode`` + the server's normalisation).
function effectiveRangeMode(f: Filters): RangeMode {
  return f.timeRange === "daily" ||
    f.timeRange === "all_time" ||
    f.timeRange === "custom"
    ? "calendar"
    : f.rangeMode;
}

// Whether the period selection is at its default (last 7 days). A
// non-default mode (e.g. Weekly + Calendar) or a custom window both
// count as a changed date filter.
function isDateDefault(f: Filters): boolean {
  return (
    f.timeRange === DEFAULT_TIME_RANGE &&
    f.rangeMode === DEFAULT_RANGE_MODE &&
    f.customRange == null
  );
}

function isFilterDefault(f: Filters): boolean {
  return (
    isDateDefault(f) &&
    f.agentId === "" &&
    f.team === "" &&
    f.transcript === DEFAULT_FILTERS.transcript &&
    f.assessed === DEFAULT_FILTERS.assessed &&
    f.direction === DEFAULT_FILTERS.direction
  );
}

function activeFilterCount(f: Filters): number {
  let n = 0;
  if (!isDateDefault(f)) n += 1;
  if (f.agentId !== "") n += 1;
  if (f.team !== "") n += 1;
  if (f.transcript !== DEFAULT_FILTERS.transcript) n += 1;
  if (f.assessed !== DEFAULT_FILTERS.assessed) n += 1;
  if (f.direction !== DEFAULT_FILTERS.direction) n += 1;
  return n;
}

// Resolve the active date range (half-open [startMs, endMs) in UTC epoch
// millis) for the Search view's client-side filtering. Delegates to the
// shared ``resolvePeriodBoundsMs`` so the window matches exactly what the
// API would compute for the same period + mode + custom range.
function resolveDateBounds(f: Filters, now: Date = new Date()): {
  startMs: number | null;
  endMs: number | null;
} {
  return resolvePeriodBoundsMs(
    f.timeRange,
    effectiveRangeMode(f),
    f.customRange,
    now,
  );
}

export function SearchView() {
  // Task #401 — which tab is active. Defaults to the phone-only Number
  // tab. Switching tabs stashes/restores per-tab state (see ``switchTab``).
  const [searchTab, setSearchTab] = useState<SearchTab>("number");
  const [raw, setRaw] = useState("");
  const [debounced, setDebounced] = useState("");
  // Task #350 — search mode tracks whether the current results came
  // from a phone-number pick or an agent pick. ``null`` = no search
  // has been run yet (or it was cleared).
  const [mode, setMode] = useState<SearchMode | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [askOpen, setAskOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Task #401 follow-up — remember each tab's last search so flipping
  // between tabs restores what you had instead of clearing it. We stash
  // the input text, the resolved search mode, and the filter bar per
  // tab; transient UI (suggestions dropdown, Ask modal) is not stashed.
  // Lives in the component, so navigating away unmounts and resets both
  // tabs — the "search resets when the user leaves" requirement is free.
  const stashRef = useRef<
    Record<SearchTab, { raw: string; mode: SearchMode | null; filters: Filters }>
  >({
    number: { raw: "", mode: null, filters: DEFAULT_FILTERS },
    agent: { raw: "", mode: null, filters: DEFAULT_FILTERS },
  });

  // 250ms debounce on the raw input. Only triggers a server call once
  // the user has typed enough characters that the suggestion list is
  // useful — otherwise a single digit would pull every phone in the DB.
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(raw.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [raw]);

  const normalized = useMemo(() => normalizePhone(debounced), [debounced]);
  const phoneQuery = useMemo(() => {
    if (normalized.kind === "invalid") return null;
    return normalized.value;
  }, [normalized]);

  // Task #401 — tab-scoped suggestion gating. The Number tab only ever
  // asks for phone suggestions, and the Agent tab only ever asks for
  // agent suggestions, so each tab does exactly one thing.
  const canSuggestPhones =
    searchTab === "number" &&
    debounced.length >= MIN_SUGGEST_CHARS &&
    phoneQuery !== null;
  const canSuggestAgents =
    searchTab === "agent" && debounced.length >= MIN_AGENT_SUGGEST_CHARS;

  const phoneSuggestionsQ = useQuery<ClientPhoneSuggestionsOut>({
    queryKey: ["client-phone-suggestions", phoneQuery],
    queryFn: () =>
      api.clientPhoneSuggestions(phoneQuery as string),
    enabled: canSuggestPhones,
    refetchInterval: false,
    staleTime: 30_000,
  });
  const agentSuggestionsQ = useQuery<ClientAgentSuggestionsOut>({
    queryKey: ["client-agent-suggestions", debounced.toLowerCase()],
    queryFn: () => api.clientAgentSuggestions(debounced),
    enabled: canSuggestAgents,
    refetchInterval: false,
    staleTime: 30_000,
  });

  const callsQ = useQuery<ClientCallsOut>({
    queryKey:
      mode == null
        ? ["client-calls", "none"]
        : mode.kind === "phone"
        ? ["client-calls", "phone", mode.phone]
        : ["client-calls", "agent", mode.agentId],
    queryFn: () => {
      if (mode == null) return Promise.resolve({ rows: [] } as ClientCallsOut);
      return mode.kind === "phone"
        ? api.clientCalls(mode.phone)
        : api.clientAgentCalls(mode.agentId);
    },
    enabled: mode != null,
    refetchInterval: false,
  });

  // Close the suggestions dropdown on outside-click so the list doesn't
  // linger while the user inspects the results table below it.
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  // Ticket 7 (prototype RAID #39) — a fresh search preserves the user's
  // date window, direction, transcript and assessed choices (so picking
  // "monthly + inbound" survives pivoting to another subject) and
  // resets only the subject-coupled filters: the agent lock and the
  // team (whose option lists derive from the new result set anyway).
  function startPhoneSearch(phone: string) {
    setFilters((prev) => ({ ...prev, agentId: "", team: "" }));
    setMode({ kind: "phone", phone });
  }

  function startAgentSearch(agent: ClientAgentSuggestionRow) {
    // Agent search pre-locks the Agent filter to the chosen agent.
    setFilters((prev) => ({ ...prev, agentId: agent.agent_id, team: "" }));
    setMode({ kind: "agent", agentId: agent.agent_id, agentName: agent.display_name });
  }

  function runSearch() {
    setShowSuggestions(false);
    // Task #401 — each tab runs exactly one kind of search. The Agent
    // tab only ever resolves to an agent; the Number tab only ever
    // resolves to a phone number.
    if (searchTab === "agent") {
      const agentFirst = agentSuggestionsQ.data?.rows?.[0];
      if (canSuggestAgents && agentFirst) {
        startAgentSearch(agentFirst);
        return;
      }
      setMode(null);
      return;
    }
    const phoneFirst = phoneSuggestionsQ.data?.rows?.[0];
    if (canSuggestPhones && phoneFirst) {
      startPhoneSearch(phoneFirst);
      return;
    }
    if (canSuggestPhones && normalized.kind === "e164") {
      startPhoneSearch(normalized.value);
      return;
    }
    setMode(null);
  }

  // Task #401 — switching tabs stashes the current tab's search and
  // restores whatever the target tab had last, so flipping back and
  // forth never loses your place. A phone result still can't show on
  // the Agent tab (or vice versa) because each tab keeps its own stash.
  function switchTab(tab: SearchTab) {
    if (tab === searchTab) return;
    // Save the current tab's state before leaving it.
    stashRef.current[searchTab] = { raw, mode, filters };
    // Restore the target tab's last state.
    const next = stashRef.current[tab];
    setSearchTab(tab);
    setRaw(next.raw);
    setDebounced(next.raw.trim());
    setMode(next.mode);
    setFilters(next.filters);
    setShowSuggestions(false);
    setAskOpen(false);
  }

  function pickPhoneSuggestion(phone: string) {
    setRaw(phone);
    setShowSuggestions(false);
    startPhoneSearch(phone);
  }

  function pickAgentSuggestion(agent: ClientAgentSuggestionRow) {
    setRaw(agent.display_name);
    setShowSuggestions(false);
    startAgentSearch(agent);
  }

  // Task #401 — gate each list by tab so a cached result from the other
  // tab can never bleed into the current dropdown.
  const phoneSuggestionRows = canSuggestPhones
    ? phoneSuggestionsQ.data?.rows ?? []
    : [];
  const agentSuggestionRows = canSuggestAgents
    ? agentSuggestionsQ.data?.rows ?? []
    : [];
  const anySuggestions =
    phoneSuggestionRows.length > 0 || agentSuggestionRows.length > 0;
  const noMatches =
    !mode &&
    ((canSuggestPhones && phoneSuggestionsQ.isSuccess) ||
      (canSuggestAgents && agentSuggestionsQ.isSuccess)) &&
    !anySuggestions;

  const allRows = useMemo(() => callsQ.data?.rows ?? [], [callsQ.data?.rows]);

  // Agent options for the dropdown are derived from whoever shows up
  // in the current result set so the user can never pick an agent who
  // has no calls for this number. In agent-mode we always include the
  // locked agent so the option exists even before the calls have
  // loaded (and the FilterBar disables the dropdown anyway).
  const agentOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of allRows) {
      const id = r.agent_id ?? "__unknown__";
      if (!seen.has(id)) {
        seen.set(id, r.agent_name ?? "Unknown");
      }
    }
    if (mode?.kind === "agent" && !seen.has(mode.agentId)) {
      seen.set(mode.agentId, mode.agentName);
    }
    return Array.from(seen.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allRows, mode]);

  // Team options follow the same "derived from currently returned rows"
  // pattern as the Agent filter. Rows with a blank team_name are
  // surfaced as an "Unassigned" bucket so they remain reachable.
  const teamOptions = useMemo(() => {
    const named = new Set<string>();
    let hasUnassigned = false;
    for (const r of allRows) {
      const t = r.team_name;
      if (t && t.trim() !== "") {
        named.add(t);
      } else {
        hasUnassigned = true;
      }
    }
    const sorted = Array.from(named).sort((a, b) => a.localeCompare(b));
    return { teams: sorted, hasUnassigned };
  }, [allRows]);

  const filteredRows = useMemo(() => {
    if (allRows.length === 0) return allRows;
    const { startMs, endMs } = resolveDateBounds(filters);
    return allRows.filter((row) => {
      if (startMs != null && endMs != null) {
        if (!row.started_at) return false;
        const t = new Date(row.started_at).getTime();
        if (Number.isNaN(t)) return false;
        // Half-open [startMs, endMs): endMs is exclusive (matches the
        // server's period bounds).
        if (t < startMs || t >= endMs) return false;
      }
      if (filters.agentId !== "") {
        const id = row.agent_id ?? "__unknown__";
        if (id !== filters.agentId) return false;
      }
      if (filters.team !== "") {
        const t = row.team_name;
        const blank = !t || t.trim() === "";
        if (filters.team === UNASSIGNED_TEAM) {
          if (!blank) return false;
        } else {
          if (blank || t !== filters.team) return false;
        }
      }
      if (filters.transcript === "with" && !row.has_transcript) return false;
      if (filters.transcript === "without" && row.has_transcript) return false;
      if (filters.assessed === "assessed" && !row.assessed) return false;
      if (filters.assessed === "not_assessed" && row.assessed) return false;
      if (filters.direction !== "all") {
        if (row.direction !== filters.direction) return false;
      }
      return true;
    });
  }, [allRows, filters]);

  const filtersActive = !isFilterDefault(filters);
  const activeCount = activeFilterCount(filters);

  // Task #350 follow-up — keep the autocomplete dropdown available even
  // after a search has been run so the user can pivot to another phone
  // or agent without first clearing the results.
  const showSuggestionsDropdown =
    showSuggestions &&
    (canSuggestPhones || canSuggestAgents) &&
    anySuggestions;

  const subjectLabel =
    mode?.kind === "agent"
      ? mode.agentName
      : mode?.kind === "phone"
      ? mode.phone
      : "";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Search</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {searchTab === "number"
            ? "Search by customer phone number to see every recorded call."
            : "Search by Agent name to see every recorded call."}
        </p>
      </div>

      <div
        role="tablist"
        aria-label="Search mode"
        className="flex items-center gap-1 border-b border-border"
      >
        {SEARCH_TABS.map((t) => (
          <TabButton
            key={t.id}
            active={searchTab === t.id}
            onClick={() => switchTab(t.id)}
            label={t.label}
          />
        ))}
      </div>

      {/* Task #396 — once a search has run, the filter bar renders ABOVE
          the search form so refining a search never requires scrolling
          past the input. */}
      {mode && allRows.length > 0 ? (
        <FilterBar
          filters={filters}
          onChange={setFilters}
          agentOptions={agentOptions}
          teamOptions={teamOptions.teams}
          hasUnassignedTeam={teamOptions.hasUnassigned}
          activeCount={activeCount}
          filtersActive={filtersActive}
          onClear={() => {
            // In agent-mode "clear" preserves the locked agent filter
            // so we don't break the mode invariant.
            if (mode?.kind === "agent") {
              setFilters({ ...DEFAULT_FILTERS, agentId: mode.agentId });
            } else {
              setFilters(DEFAULT_FILTERS);
            }
          }}
          agentLocked={mode?.kind === "agent"}
          agentLockName={mode?.kind === "agent" ? mode.agentName : null}
        />
      ) : null}

      <div ref={wrapperRef} className="relative max-w-xl">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            runSearch();
          }}
          className="flex items-stretch gap-2"
        >
          <div className="relative flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <input
              type="search"
              autoComplete="off"
              value={raw}
              placeholder={
                searchTab === "number"
                  ? "Enter a client phone number"
                  : "Enter an agent name"
              }
              onChange={(e) => {
                setRaw(e.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              aria-label={
                searchTab === "number"
                  ? "Customer phone number"
                  : "Agent name"
              }
              className="w-full rounded-md border border-input bg-background py-2 pl-9 pr-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
          <button
            type="submit"
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Search
          </button>
          {mode && filteredRows.length > 0 ? (
            <button
              type="button"
              onClick={() => setAskOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <MessageSquare className="h-4 w-4" aria-hidden="true" />
              Ask a Question
            </button>
          ) : null}
        </form>

        {showSuggestionsDropdown ? (
          <ul
            role="listbox"
            className="absolute left-0 right-0 z-10 mt-1 max-h-64 overflow-auto rounded-md border border-border bg-popover shadow-md"
          >
            {agentSuggestionRows.map((a) => (
              <li
                key={`agent-${a.agent_id}`}
                role="option"
                aria-selected="false"
              >
                <button
                  type="button"
                  onClick={() => pickAgentSuggestion(a)}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-muted focus:bg-muted focus:outline-none"
                >
                  <span className="truncate">{a.display_name}</span>
                  <span className="shrink-0 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
                    Agent
                  </span>
                </button>
              </li>
            ))}
            {phoneSuggestionRows.map((phone) => (
              <li
                key={`phone-${phone}`}
                role="option"
                aria-selected="false"
              >
                <button
                  type="button"
                  onClick={() => pickPhoneSuggestion(phone)}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-muted focus:bg-muted focus:outline-none"
                >
                  <span className="truncate">{phone}</span>
                  <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    Phone
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {mode ? (
        <CallsTable
          subjectLabel={subjectLabel}
          subjectKind={mode.kind}
          isLoading={callsQ.isLoading}
          error={
            callsQ.error
              ? (callsQ.error as { message?: string })?.message ||
                "Failed to load call history"
              : null
          }
          rows={filteredRows}
          totalRows={allRows.length}
          filtersActive={filtersActive}
        />
      ) : noMatches ? (
        <p className="text-sm text-muted-foreground italic">
          No records found
        </p>
      ) : null}

      {askOpen && mode ? (
        <AskQuestionModal
          mode={mode}
          calls={filteredRows}
          onClose={() => setAskOpen(false)}
        />
      ) : null}
    </div>
  );
}

// Task #401 — tab header following the house tab pattern (see
// TrainingTipsModal's TabButton): underline indicator via border-b-2,
// primary border + foreground text when active, muted otherwise.
function TabButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={
        "rounded-t border-b-2 px-3 py-1.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
        (active
          ? "border-primary text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground")
      }
    >
      {label}
    </button>
  );
}

// Task #336 — Ask-a-Question modal.
// Task #350 — modal works for both phone-mode and agent-mode; the
// header copy and request payload vary by ``mode.kind``.
// PET-642 (port of prototype Task #440) — the modal is now a running
// chat: each Q&A is appended as a ``turn`` and the whole thread stays
// visible for the life of the modal, with a header Copy button that
// exports the thread as rich text (HTML + plain-text clipboard payload).
// All state lives inside this component and the component only mounts
// while the modal is open (see the ``askOpen && mode`` render gate in
// SearchView), so close → reopen resets the thread for free.
// T8 (prototype dfa1606) — Ask a Question pools at most this many calls
// (the most recent ones within the active filter). The backend enforces
// the same cap independently (_CLIENT_QA_MAX_CALLS).
const ASK_MAX_CALLS = 100;

function AskQuestionModal({
  mode,
  calls,
  onClose,
}: {
  mode: SearchMode;
  calls: ClientCallRow[];
  onClose: () => void;
}) {
  // T8 — the modal receives the full filtered rows so it can compute the
  // date range spanned by the calls actually used; ids derive from them.
  const callIds = useMemo(() => calls.map((c) => c.call_id), [calls]);
  // The answer travels with its call_refs so call-ID citations render as
  // "<phone> (<ordinal>)" links (PET-645). The refMap is derived from
  // ``callRefs`` ONCE, when the turn is appended — ordinals are scoped to
  // that answer's analysed call pool, so each turn keeps its own map and
  // the same call_id may carry different ordinals in different turns.
  type QaTurn = {
    question: string;
    answer: string;
    callRefs: CallRef[];
    refMap: CallRefMap;
    // T8 — server-truth analysed-call count for this turn. The server
    // number is authoritative: visibility filtering can shrink the pool
    // below the ids the client sent.
    callsUsed: number;
  };
  const [question, setQuestion] = useState("");
  const [turns, setTurns] = useState<QaTurn[]>([]);
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // Copy-button feedback: "copied" on success, "failed" when the
  // clipboard write didn't land (no clipboard API in non-secure
  // contexts, or a permission rejection). Both reset to idle after 2s.
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle",
  );
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const copiedTimerRef = useRef<number | null>(null);

  // PET-641 — admin-curated preset questions rendered as clickable
  // bubbles above the input row. A click populates the input (never
  // auto-sends) so the preset can be edited and an accidental click
  // never burns an LLM call. Nothing renders while loading / when the
  // list is empty. The admin editor invalidates this key on save, so
  // reopening the modal shows the new list without a hard refresh.
  const presetsQ = useQuery<SuggestedQuestionsOut>({
    queryKey: ["suggested-questions"],
    queryFn: () => api.suggestedQuestions(),
  });
  const presets = presetsQ.data?.questions ?? [];

  // Close on Escape.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Focus the input on mount (isLoading starts false) and refocus it
  // whenever an ask settles — success or error. The textarea is
  // ``disabled`` while an ask is in flight, so a synchronous focus()
  // inside send() would be defeated on the very next render; focus has
  // to be restored after the disable lifts.
  useEffect(() => {
    if (!isLoading) inputRef.current?.focus();
  }, [isLoading]);

  // Keep the newest turn (or the in-flight question / inline error) in
  // view as the conversation grows. The body is the scroll container.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns.length, pendingQuestion, isLoading, error]);

  // Clear the copy-feedback timer on unmount so closing the modal
  // within the 2s window can't fire a setState on an unmounted component.
  useEffect(() => {
    return () => {
      if (copiedTimerRef.current != null) {
        window.clearTimeout(copiedTimerRef.current);
      }
    };
  }, []);

  const tooManyCalls = callIds.length > ASK_MAX_CALLS;
  const canSend = question.trim().length > 0 && !isLoading;
  const canCopy = turns.length > 0;

  // T8 — date range spanned by the calls actually used (the most recent
  // ASK_MAX_CALLS within scope). Rows arrive most-recent-first.
  const usedDateRange = useMemo(() => {
    const times = calls
      .slice(0, ASK_MAX_CALLS)
      .map((c) => c.started_at)
      .filter((s): s is string => !!s)
      .map((s) => new Date(s).getTime())
      .filter((t) => !Number.isNaN(t));
    if (times.length === 0) return null;
    return { earliest: Math.min(...times), latest: Math.max(...times) };
  }, [calls]);

  async function send() {
    const q = question.trim();
    if (!q || isLoading) return;
    setIsLoading(true);
    setError(null);
    setPendingQuestion(q);
    // Clear the input immediately so the next question can be lined up
    // as soon as this one resolves. Refocus happens in the isLoading
    // effect above — the textarea is disabled while in flight, so a
    // synchronous focus() here would not survive the next render.
    setQuestion("");
    try {
      // Cap client-side so the user gets the answer they expect. The
      // server enforces the same cap independently.
      const ids = callIds.slice(0, ASK_MAX_CALLS);
      const res = await api.clientQa({
        phone: mode.kind === "phone" ? mode.phone : null,
        agent_id: mode.kind === "agent" ? mode.agentId : null,
        question: q,
        call_ids: ids,
      });
      const callRefs = res.call_refs ?? [];
      setTurns((prev) => [
        ...prev,
        {
          question: q,
          answer: res.answer,
          callRefs,
          refMap: buildCallRefMap(callRefs),
          callsUsed: res.calls_used,
        },
      ]);
    } catch (e) {
      const msg =
        (e as ApiError)?.message ||
        (e as Error)?.message ||
        "Failed to get an answer";
      setError(String(msg));
      // A failed ask puts the question back in the input so it can be
      // edited and resent — but never clobbers anything the user has
      // already typed for the next attempt.
      setQuestion((cur) => (cur.trim().length > 0 ? cur : q));
    } finally {
      setPendingQuestion(null);
      setIsLoading(false);
    }
  }

  async function copyConversation() {
    if (!canCopy) return;
    let ok = false;
    try {
      const { html, text } = buildQaCopyPayload(turns, mode, callIds.length);
      ok = await writeRichClipboard(html, text);
    } catch {
      // Clipboard can reject (permissions / focus loss). Fall through to
      // the "Copy failed" state — silent failure left users thinking the
      // thread was on the clipboard when nothing was written.
    }
    setCopyState(ok ? "copied" : "failed");
    if (copiedTimerRef.current != null) {
      window.clearTimeout(copiedTimerRef.current);
    }
    copiedTimerRef.current = window.setTimeout(
      () => setCopyState("idle"),
      2000,
    );
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Ask a question about these calls"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-border bg-background shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
          <div>
            <h2 className="text-base font-semibold">Ask a Question</h2>
            <p className="text-xs text-muted-foreground">
              About the {callIds.length} call
              {callIds.length === 1 ? "" : "s"} shown for{" "}
              {mode.kind === "phone" ? mode.phone : mode.agentName}
            </p>
          </div>
          <div className="flex items-center gap-1">
            {canCopy ? (
              <button
                type="button"
                onClick={() => void copyConversation()}
                // Port-plan Step 5.4 — Copy is disabled while an ask is
                // in flight so the export can't race a turn being added.
                disabled={isLoading}
                className="hover-elevate inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-medium text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              >
                {copyState === "copied" ? (
                  <Check className="h-3.5 w-3.5" aria-hidden="true" />
                ) : copyState === "failed" ? (
                  <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                ) : (
                  <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                {copyState === "copied"
                  ? "Copied"
                  : copyState === "failed"
                    ? "Copy failed"
                    : "Copy"}
              </button>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>

        {tooManyCalls ? (
          <div className="border-b border-border bg-amber-500/10 px-4 py-2 text-xs text-amber-900 dark:text-amber-200">
            Ask a Question focuses on the {ASK_MAX_CALLS} most recent calls
            to keep answers sharp. You have {callIds.length} calls in scope,
            so the most recent {ASK_MAX_CALLS} will be used
            {usedDateRange
              ? ` (spanning ${fmtDateTime(
                  new Date(usedDateRange.earliest),
                )} to ${fmtDateTime(new Date(usedDateRange.latest))})`
              : ""}
            .
          </div>
        ) : null}

        <div
          ref={scrollRef}
          className="flex-1 space-y-4 overflow-y-auto px-4 py-4 text-sm"
        >
          {turns.length === 0 && !pendingQuestion && !error ? (
            <div className="text-muted-foreground italic">
              Ask anything about these calls — for example, "What were the
              main objections?" or "Did the agent confirm next steps?"
            </div>
          ) : null}

          {turns.map((t, i) => (
            <div key={i} className="space-y-1.5">
              <div className="font-medium text-foreground">{t.question}</div>
              <div className="whitespace-pre-wrap leading-relaxed text-muted-foreground">
                {linkifyCallIds(t.answer, `qa-${i}`, t.refMap)}
              </div>
              {/* T8 — server-truth analysed count; authoritative when
                  visibility filtering shrinks the pool below the ids sent. */}
              <div className="text-xs text-muted-foreground">
                Analysed {t.callsUsed} call{t.callsUsed === 1 ? "" : "s"}
              </div>
            </div>
          ))}

          {pendingQuestion ? (
            <div className="space-y-1.5">
              <div className="font-medium text-foreground">
                {pendingQuestion}
              </div>
              {isLoading ? (
                <div className="text-muted-foreground italic">Thinking…</div>
              ) : null}
            </div>
          ) : null}

          {error ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-destructive">
              {error}
            </div>
          ) : null}
        </div>

        <div className="border-t border-border p-3">
          {presets.length > 0 ? (
            // PET-672 — bubbles are grouped by colour; each group is one
            // wrapped row prefixed with a small colour dot. The list is
            // already team-scoped server-side (the chat-surface GET).
            <div className="mb-2 space-y-2">
              {groupByColor(presets, (s) => s.color).map((group) => (
                <div
                  key={group.color ?? "none"}
                  className="flex flex-wrap items-center gap-1.5"
                >
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${colorStyle(group.color).dot}`}
                    aria-hidden="true"
                  />
                  {group.items.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      // Long presets are visually truncated (max-w mirrors the
                      // DESIGN.md tooltip width cap); the title tooltip carries
                      // the full text. Port-plan Step 6 improvement.
                      title={s.text}
                      disabled={isLoading}
                      onClick={() => {
                        setQuestion(s.text);
                        inputRef.current?.focus();
                      }}
                      className={`max-w-[18rem] truncate rounded-full border px-3 py-1 text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 ${colorStyle(s.color).bubble}`}
                    >
                      {s.text}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          ) : null}
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Type your question…"
              rows={2}
              disabled={isLoading}
              className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
            />
            <button
              type="button"
              onClick={() => void send()}
              disabled={!canSend}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Send className="h-4 w-4" aria-hidden="true" />
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FilterBar({
  filters,
  onChange,
  agentOptions,
  teamOptions,
  hasUnassignedTeam,
  activeCount,
  filtersActive,
  onClear,
  agentLocked = false,
  agentLockName = null,
}: {
  filters: Filters;
  onChange: (next: Filters) => void;
  agentOptions: { id: string; name: string }[];
  teamOptions: string[];
  hasUnassignedTeam: boolean;
  activeCount: number;
  filtersActive: boolean;
  onClear: () => void;
  agentLocked?: boolean;
  agentLockName?: string | null;
}) {
  const update = <K extends keyof Filters>(key: K, value: Filters[K]) =>
    onChange({ ...filters, [key]: value });

  const selectClass =
    "rounded-md border border-input bg-background px-2 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  const labelClass =
    "text-xs font-medium uppercase tracking-wide text-muted-foreground";

  // Task #425 — period selection now flows through the shared
  // TimeRangeControls. Switching presets preserves the Calendar/Rolling
  // choice and clears any stale custom window; picking a custom range
  // pins ``timeRange`` to "custom".
  const onTimeRangeChange = (next: TimeRange) => {
    onChange({
      ...filters,
      timeRange: next,
      customRange: next === "custom" ? filters.customRange : null,
    });
  };
  const onRangeModeChange = (next: RangeMode) =>
    update("rangeMode", next);
  const onCustomRangeChange = (range: CustomDateRange) =>
    onChange({ ...filters, timeRange: "custom", customRange: range });

  return (
    <div className="rounded-lg border border-border bg-card p-3 shadow-sm">
      <div className="flex flex-col gap-3">
        {/* Top row — the shared period selector sits on its own line so
            the custom picker popover never pushes the other filters
            sideways. Matches the Teams / Agent / Agent Profile views. */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <span className={labelClass}>Date range</span>
            <TimeRangeControls
              value={filters.timeRange}
              rangeMode={filters.rangeMode}
              customRange={filters.customRange}
              onValueChange={onTimeRangeChange}
              onRangeModeChange={onRangeModeChange}
              onCustomRangeChange={onCustomRangeChange}
            />
          </div>
        </div>

        {/* Bottom row — the remaining filters keep a stable position and
            size regardless of the date selection above. */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className={labelClass} htmlFor="search-filter-agent">
              Agent
            </label>
            <select
              id="search-filter-agent"
              value={filters.agentId}
              onChange={(e) => update("agentId", e.target.value)}
              className={selectClass}
              disabled={agentLocked}
              aria-disabled={agentLocked || undefined}
              title={
                agentLocked && agentLockName
                  ? `Locked to ${agentLockName} for this search`
                  : undefined
              }
            >
              {agentLocked ? null : <option value="">All agents</option>}
              {agentOptions.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>

          {teamOptions.length > 0 || hasUnassignedTeam ? (
            <div className="flex flex-col gap-1">
              <label className={labelClass} htmlFor="search-filter-team">
                Team
              </label>
              <select
                id="search-filter-team"
                value={filters.team}
                onChange={(e) => update("team", e.target.value)}
                className={selectClass}
              >
                <option value="">All teams</option>
                {teamOptions.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
                {hasUnassignedTeam ? (
                  <option value={UNASSIGNED_TEAM}>Unassigned</option>
                ) : null}
              </select>
            </div>
          ) : null}

          <div className="flex flex-col gap-1">
            <label className={labelClass} htmlFor="search-filter-direction">
              Direction
            </label>
            <select
              id="search-filter-direction"
              value={filters.direction}
              onChange={(e) =>
                update("direction", e.target.value as DirectionFilter)
              }
              className={selectClass}
            >
              <option value="all">All</option>
              <option value="inbound">Inbound</option>
              <option value="outbound">Outbound</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className={labelClass} htmlFor="search-filter-transcript">
              Transcript
            </label>
            <select
              id="search-filter-transcript"
              value={filters.transcript}
              onChange={(e) =>
                update("transcript", e.target.value as TranscriptFilter)
              }
              className={selectClass}
            >
              <option value="all">All</option>
              <option value="with">With transcript</option>
              <option value="without">Without transcript</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className={labelClass} htmlFor="search-filter-assessed">
              Assessed
            </label>
            <select
              id="search-filter-assessed"
              value={filters.assessed}
              onChange={(e) =>
                update("assessed", e.target.value as AssessedFilter)
              }
              className={selectClass}
            >
              <option value="all">All</option>
              <option value="assessed">Assessed</option>
              <option value="not_assessed">Not assessed</option>
            </select>
          </div>

          <div className="ml-auto flex items-center gap-2">
            {filtersActive ? (
              <span
                className="inline-flex items-center rounded-full bg-primary/15 px-2 py-0.5 text-xs font-medium text-primary"
                aria-label={`${activeCount} active filter${activeCount === 1 ? "" : "s"}`}
              >
                {activeCount} active
              </span>
            ) : null}
            {filtersActive ? (
              <button
                type="button"
                onClick={onClear}
                className="rounded-md border border-input bg-background px-3 py-1.5 text-sm text-foreground hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Clear filters
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function CallsTable({
  subjectLabel,
  subjectKind,
  isLoading,
  error,
  rows,
  totalRows,
  filtersActive,
}: {
  subjectLabel: string;
  subjectKind: "phone" | "agent";
  isLoading: boolean;
  error: string | null;
  rows: ClientCallRow[];
  totalRows: number;
  filtersActive: boolean;
}) {
  const noHistoryCopy =
    subjectKind === "agent"
      ? "No call history found for this agent"
      : "No call history found for this number";
  if (isLoading) {
    return (
      <div className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
        Loading call history…
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
        {error}
      </div>
    );
  }
  if (totalRows === 0) {
    return (
      <div className="space-y-2">
        <div className="text-sm text-muted-foreground">
          Showing calls for{" "}
          <span className="font-medium text-foreground">{subjectLabel}</span>
        </div>
        <p className="text-sm text-muted-foreground italic">
          {noHistoryCopy}
        </p>
      </div>
    );
  }
  const filteredOut = filtersActive && rows.length === 0;
  return (
    <div className="space-y-3">
      <div className="text-sm text-muted-foreground">
        {filtersActive ? (
          <>
            {rows.length} of {totalRows} call{totalRows === 1 ? "" : "s"} for{" "}
            <span className="font-medium text-foreground">{subjectLabel}</span>
          </>
        ) : (
          <>
            {rows.length} call{rows.length === 1 ? "" : "s"} for{" "}
            <span className="font-medium text-foreground">{subjectLabel}</span>
          </>
        )}
      </div>
      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
            {/* T9 (#397) — Phone column identifies the customer per row;
                the Team column is dropped (team_name stays in the
                payload to keep the Team filter working). Still 8
                columns, so colSpan below stays 8. */}
            <tr>
              <th className="px-3 py-2 font-medium">Call ID</th>
              <th className="px-3 py-2 font-medium">Phone</th>
              <th className="px-3 py-2 font-medium">Started</th>
              <th className="px-3 py-2 font-medium">Direction</th>
              <th className="px-3 py-2 font-medium">Agent</th>
              <th className="px-3 py-2 font-medium">Duration</th>
              <th className="px-3 py-2 font-medium">Transcript</th>
              <th className="px-3 py-2 font-medium">Assessed</th>
            </tr>
          </thead>
          <tbody>
            {filteredOut ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-3 py-6 text-center text-sm text-muted-foreground italic"
                >
                  No calls match the current filters
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.call_id} className="border-t border-border">
                  <td className="px-3 py-2">
                    <a
                      href={buildUrl("call-detail", null, null, {
                        callId: row.call_id,
                      })}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                    >
                      {row.call_id.slice(0, 8)}
                      <ExternalLink className="h-3 w-3" aria-hidden="true" />
                    </a>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {row.contact_phone?.trim() ? row.contact_phone : "—"}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {row.started_at ? fmtDateTime(row.started_at) : "—"}
                  </td>
                  <td className="px-3 py-2 capitalize">
                    {row.direction ?? "—"}
                  </td>
                  <td className="px-3 py-2">{row.agent_name ?? "—"}</td>
                  <td className="px-3 py-2 tabular-nums">
                    {row.duration_ms != null
                      ? fmtDurationMs(row.duration_ms)
                      : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <YesNo value={row.has_transcript} />
                  </td>
                  <td className="px-3 py-2">
                    <YesNo value={row.assessed} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function YesNo({ value }: { value: boolean }) {
  return value ? (
    <span className="inline-flex items-center gap-1 text-primary">
      <Check className="h-4 w-4" aria-hidden="true" />
      <span className="sr-only">Yes</span>
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-muted-foreground">
      <X className="h-4 w-4" aria-hidden="true" />
      <span className="sr-only">No</span>
    </span>
  );
}
